import { GeneralTooltip } from '@components/common/GeneralTooltip';
import MaterialIcon from '@components/common/MaterialIcon';
import clsx from 'clsx';
import type { FieldValues } from 'react-hook-form';

type BadgeProps<_T extends FieldValues> = {
  text: string;
  icon?: string;
  iconClasses?: string;
  bgColorHex?: string;
  fontColorHex?: string;
  onRemove?: () => void;
  shouldOverrideTextSize?: boolean;
  tooltip?: string;
  /** When true, long text is truncated with an ellipsis. A title attribute is added so the full text is visible on hover. */
  truncate?: boolean;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLSpanElement>,
  HTMLSpanElement
>;

export function BadgeT<T extends FieldValues>({
  text,
  style,
  bgColorHex,
  fontColorHex,
  onRemove,
  icon,
  iconClasses = '',
  shouldOverrideTextSize,
  tooltip,
  truncate = false,
  ...restParams
}: BadgeProps<T>) {
  const { className, ...rest } = restParams;
  const defaultStyles = [
    'flex',
    'items-center',
    'px-2',
    'py-0.5',
    'rounded-2xl',
    'font-normal',
    truncate ? 'min-w-0 overflow-hidden' : 'w-fit whitespace-nowrap',
    'max-w-full',
    'overflow-ellipsis',
    'gap-1',
    'z-[99]',
    !fontColorHex && 'text-white',
  ];

  if (!shouldOverrideTextSize) {
    defaultStyles.push('text-xs');
  }

  // Note: In the case of Tailwind CSS 3.x, arbitrary values cannot be computed from dynamic values (e.g., bg-[{{ hexCode }}]). That's why we need to add the backgroundColor to the style attribute.
  const combinedStyle = {
    backgroundColor: bgColorHex,
    ...style,
  };

  // Similarly, we need to add the color to the style attribute if fontColorHex is provided.
  if (fontColorHex) {
    combinedStyle.color = fontColorHex;
  }

  let iconClassesArray = ['text-base', 'leading-none'];
  if (iconClasses) {
    iconClassesArray = iconClasses.split(' ');
  }

  return (
    <span
      className={clsx([...defaultStyles, className])}
      style={combinedStyle}
      title={truncate ? text : undefined}
      {...rest}>
      {icon && <MaterialIcon icon={icon} classes={iconClassesArray} />}
      {truncate ? (
        <span className='flex-1 min-w-0 truncate'>{text}</span>
      ) : (
        text
      )}
      {tooltip && (
        <GeneralTooltip
          text={tooltip}
          className='text-timestamp xs:text-pico !w-[175px] !top-7 font-thin'
          parentClassName='word-break-style whitespace-break-spaces'
          placement='bottom'>
          <MaterialIcon icon='info' classes={['text-base']} />
        </GeneralTooltip>
      )}
      {onRemove && (
        <span
          aria-hidden='true'
          className='ml-1 cursor-pointer text-sm leading-none'
          onClick={() => onRemove()}>
          &times;
        </span>
      )}
    </span>
  );
}

export default BadgeT;
